import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { learningAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { 
  BookOpen, CheckCircle2, Circle, Play, Trophy,
  ChevronDown, ChevronUp, Clock, FileText, Video,
  ArrowLeft, Target
} from 'lucide-react';

const CourseProgress = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [updatingLesson, setUpdatingLesson] = useState(null);

  useEffect(() => {
    loadProgress();
  }, [courseId]);

  useEffect(() => {
    if (progress?.course) {
      document.title = `${progress.course.title} - İlerleme | İlkadım360 Hub`;
    }
  }, [progress]);

  const loadProgress = async () => {
    try {
      const response = await learningAPI.getCourseProgress(courseId);
      setProgress(response.data);
      
      // Expand all sections by default
      const expanded = {};
      response.data.sections.forEach(s => {
        expanded[s.section.id] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('İlerleme yüklenemedi:', error);
      if (error.response?.status === 404) {
        navigate('/learning/my-progress');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLessonToggle = async (lessonId, isCurrentlyCompleted) => {
    setUpdatingLesson(lessonId);
    try {
      if (isCurrentlyCompleted) {
        await learningAPI.unmarkLessonComplete(lessonId);
      } else {
        await learningAPI.markLessonComplete(lessonId);
      }
      await loadProgress();
    } catch (error) {
      console.error('Ders durumu güncellenemedi:', error);
    } finally {
      setUpdatingLesson(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Kurs ilerleme bilgisi bulunamadı</p>
      </div>
    );
  }

  const { course, sections, totalLessons, completedLessons, progressPercent, isCompleted } = progress;

  return (
    <div data-testid="course-progress-page">
      <Breadcrumb 
        items={[
          { label: 'Online Eğitim', path: '/learning' },
          { label: 'İlerlemem', path: '/learning/my-progress' },
          { label: course.title, path: null }
        ]} 
      />

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
              {isCompleted && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <Trophy size={16} />
                  Tamamlandı
                </span>
              )}
            </div>
            <p className="text-gray-600">{course.description}</p>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{progressPercent}%</p>
            <p className="text-sm text-blue-700">Tamamlanan</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{completedLessons}</p>
            <p className="text-sm text-green-700">Tamamlanan Ders</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-gray-600">{totalLessons - completedLessons}</p>
            <p className="text-sm text-gray-600">Kalan Ders</p>
          </div>
        </div>

        {/* Main Progress Bar */}
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Genel İlerleme</span>
            <span className="font-medium">{completedLessons} / {totalLessons} ders</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                isCompleted ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Sections & Lessons */}
      <div className="space-y-4">
        {sections.map((sectionData, index) => (
          <SectionCard 
            key={sectionData.section.id}
            sectionData={sectionData}
            index={index}
            isExpanded={expandedSections[sectionData.section.id]}
            onToggle={() => toggleSection(sectionData.section.id)}
            onLessonToggle={handleLessonToggle}
            updatingLesson={updatingLesson}
            onLessonClick={(lessonId) => navigate(`/learning/courses/${courseId}/lessons/${lessonId}`)}
          />
        ))}
      </div>

      {/* Completion Message */}
      {isCompleted && (
        <div className="mt-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white text-center">
          <Trophy className="mx-auto mb-3" size={48} />
          <h3 className="text-xl font-bold mb-2">Tebrikler! 🎉</h3>
          <p className="opacity-90">Bu kursu başarıyla tamamladınız.</p>
        </div>
      )}
    </div>
  );
};

// Section Card Component
const SectionCard = ({ 
  sectionData, 
  index, 
  isExpanded, 
  onToggle, 
  onLessonToggle,
  updatingLesson,
  onLessonClick
}) => {
  const { section, lessons, totalLessons, completedLessons, progressPercent } = sectionData;
  const isAllCompleted = completedLessons === totalLessons && totalLessons > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Section Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
            isAllCompleted 
              ? 'bg-green-100 text-green-600' 
              : 'bg-blue-100 text-blue-600'
          }`}>
            {isAllCompleted ? <CheckCircle2 size={20} /> : index + 1}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{section.title}</h3>
            <p className="text-sm text-gray-500">
              {completedLessons} / {totalLessons} ders tamamlandı
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  isAllCompleted ? 'bg-green-500' : 'bg-blue-500'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-600 w-12">{progressPercent}%</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="text-gray-400" size={20} />
          ) : (
            <ChevronDown className="text-gray-400" size={20} />
          )}
        </div>
      </div>

      {/* Lessons List */}
      {isExpanded && (
        <div className="border-t divide-y">
          {lessons.map((lesson) => (
            <LessonRow 
              key={lesson.id}
              lesson={lesson}
              onToggle={() => onLessonToggle(lesson.id, lesson.isCompleted)}
              isUpdating={updatingLesson === lesson.id}
              onClick={() => onLessonClick(lesson.id)}
            />
          ))}
          {lessons.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              Bu bölümde henüz ders yok
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Lesson Row Component
const LessonRow = ({ lesson, onToggle, isUpdating, onClick }) => {
  const getLessonIcon = () => {
    if (lesson.contentType === 'VIDEO') return <Video size={18} className="text-purple-500" />;
    if (lesson.contentType === 'DOCUMENT') return <FileText size={18} className="text-orange-500" />;
    return <BookOpen size={18} className="text-blue-500" />;
  };

  return (
    <div className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors group">
      <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={onClick}>
        <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
          {getLessonIcon()}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
            {lesson.title}
          </h4>
          {lesson.duration && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <Clock size={14} />
              <span>{lesson.duration} dk</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Completion Toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        disabled={isUpdating}
        className={`p-2 rounded-lg transition-all ${
          lesson.isCompleted 
            ? 'text-green-600 hover:bg-green-100' 
            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
        } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={lesson.isCompleted ? 'Tamamlanmadı olarak işaretle' : 'Tamamlandı olarak işaretle'}
        data-testid={`lesson-toggle-${lesson.id}`}
      >
        {isUpdating ? (
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-current border-t-transparent" />
        ) : lesson.isCompleted ? (
          <CheckCircle2 size={24} />
        ) : (
          <Circle size={24} />
        )}
      </button>
    </div>
  );
};

export default CourseProgress;
